import React from "react";
import { Box, Button, Center, Flex, Select, Stack } from "@chakra-ui/react";
import DeleteOutline from "@material-ui/icons/DeleteOutline";
import { FieldArray, FieldArrayRenderProps } from "formik";
import { Group } from "./Group";
import { useConditionGroups } from "../hooks/useConditionGroups";
import { canRemoveGroup } from "../utils/conditionUtils";
import { IEnvironmentalVariable } from "stores/data/oceanWeatherIntelligences/oceanWeatherIntelligenceModel";
import { ConditionGroup } from "stores/data/insightAlerts/insightAlertModel";
import AddIcon from "@material-ui/icons/Add";

interface ConditionBuilderProps {
  conditionGroups: ConditionGroup[];
  environmentalVariables?: IEnvironmentalVariable[];
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
  t: (key: string) => string;
  errors?: any;
  /** Formik field name for the FieldArray. Defaults to "conditionGroups". */
  fieldArrayName?: string;
  /** Default environmental variable ID for new conditions. Defaults to 7 (WIND_VEL). */
  defaultEnvironmentalVariableId?: number;
}

export const ConditionBuilder: React.FC<ConditionBuilderProps> = ({
  conditionGroups,
  environmentalVariables,
  setFieldValue,
  t,
  errors,
  fieldArrayName = "conditionGroups",
  defaultEnvironmentalVariableId,
}) => {
  const {
    addConditionToGroup,
    removeConditionFromGroup,
    addNewGroup,
    removeGroup,
    updateGroupOperator,
    updateConditionOperator,
    updateCondition,
  } = useConditionGroups({
    conditionGroups,
    onConditionGroupsChange: setFieldValue,
    environmentalVariables,
    fieldName: fieldArrayName,
    ...(defaultEnvironmentalVariableId !== undefined && { defaultEnvironmentalVariableId }),
  });

  // Resolve errors subtree for the field array path (e.g. "sensorAlertConfig.conditions" → errors.sensorAlertConfig.conditions)
  const fieldErrors = fieldArrayName
    .split(".")
    .reduce((obj: any, key: string) => obj?.[key], errors);

  return (
    <Stack spacing={6}>
      <FieldArray name={fieldArrayName}>
        {(groupHelpers: FieldArrayRenderProps) => (
          <Stack spacing={4}>
            {conditionGroups.map((group, groupIndex) => (
              <Box
                key={group.id}
                p={4}
                mt={3}
                bg="#ffffff"
                borderRadius={8}
                alignItems="center"
                position="relative"
              >
                {groupIndex > 0 && (
                  <Center mt={-10} mb={2}>
                    <Select
                      width="80px"
                      borderRadius={8}
                      backgroundColor="#ffffff"
                      borderColor="newBlue.500"
                      fontSize={14}
                      size="sm"
                      color="rgba(30, 82, 151, 0.8)"
                      _focus={{ borderColor: "none" }}
                      value={conditionGroups[groupIndex - 1].groupOperator}
                      onChange={(e) =>
                        updateGroupOperator(groupIndex - 1, e.target.value)
                      }
                    >
                      <option value={"and"}>{t("and")}</option>
                      <option value={"or"}>{t("or")}</option>
                    </Select>
                  </Center>
                )}

                {canRemoveGroup(conditionGroups, groupIndex) && (
                  <Flex position="absolute" top="16px" right="16px">
                    <Button
                      layerStyle="lightRedOcean"
                      _hover={{
                        layerStyle: "lightRedOcean",
                      }}
                      leftIcon={<DeleteOutline />}
                      size="sm"
                      onClick={() => {
                        removeGroup(groupIndex);
                      }}
                    >
                      {t("removeGroup")}
                    </Button>
                  </Flex>
                )}

                <Group
                  group={group}
                  groupIndex={groupIndex}
                  environmentalVariables={environmentalVariables}
                  onConditionOperatorChange={(value) =>
                    updateConditionOperator(groupIndex, value)
                  }
                  onConditionUpdate={(conditionIndex, field, value) =>
                    updateCondition(groupIndex, conditionIndex, field, value)
                  }
                  onAddCondition={() => addConditionToGroup(groupIndex)}
                  onRemoveCondition={(conditionIndex) =>
                    removeConditionFromGroup(groupIndex, conditionIndex)
                  }
                  errors={fieldErrors}
                  t={t}
                  fieldArrayName={fieldArrayName}
                />
              </Box>
            ))}

            <Box>
              <Button
                layerStyle="lightOcean"
                _hover={{
                  layerStyle: "lightOcean",
                }}
                onClick={() => {
                  addNewGroup();
                }}
                leftIcon={<AddIcon />}
              >
                {t("addNewGroup")}
              </Button>
            </Box>
          </Stack>
        )}
      </FieldArray>
    </Stack>
  );
};
